import { useAuth0 } from '@auth0/auth0-react'
import DeleteIcon from '@mui/icons-material/Delete'
import EditIcon from '@mui/icons-material/Edit'
import LoadingButton from '@mui/lab/LoadingButton'
import { Box, Button, Divider, IconButton, Stack, Typography } from '@mui/material'
import { DatePicker } from '@mui/x-date-pickers'
import { useAtomValue } from 'jotai'
import { DateTime } from 'luxon'
import { FC, useMemo, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { Link as ReactRouterLink, useNavigate, useParams } from 'react-router-dom'
import { useCreateLocationChange, useLocation, useUpdateLocationChange } from '../../../api/apiQueries'
import { CreateLocationChange, LocationChangeState, MonitoringLocationChangeAddress, UpdateLocationChange } from '../../../api/models'
import { addressValidationResultAtom, currentLocationChangeAtom, selectedAddressValidationResultIndexAtom } from '../../../atoms'
import { useAuthenticationContext } from '../../../auth/AuthenticationContextProvider'
import { Page } from '../../../components/Page'
import { DELETE_LOCATION_CHANGE_SCOPE } from '../../../constants'
import { DeleteSchedulerDialog } from './DeleteSchedulerDialog'
import { ValidationStatusChip } from './ValidationStatusChip'
import { useSnackbar } from '../../../hooks/useSnackbar'
import { LoggerName, namedLogger } from '../../../utils/logger'

const logger = namedLogger(LoggerName.DatePickerPage)
import { sendCreateLocationChangeMetric, sendDeleteLocationChangeMetric, sendUpdateLocationChangeMetric } from './metricsUtils'
import { addressValidationPaths } from '../addressValidationPaths'
import { toLocationChangeWithAddress } from '../AddressSchedulerFormPage/addressScheduleFormUtils'

interface FormInputs {
  scheduledDate: DateTime | null
}

const DatePickerPage: FC = () => {
  const { toast } = useSnackbar()

  const { user } = useAuth0()
  const { sid: locationId, locationChangeId } = useParams()
  const locationQuery = useLocation(locationId)
  const location = locationQuery.data
  const navigate = useNavigate()
  const createLocationChangeMutation = useCreateLocationChange()
  const updateLocationChangeMutation = useUpdateLocationChange()
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [isDeleting, setIsDeleting] = useState(false)
  const addressValidationResults = useAtomValue(addressValidationResultAtom)
  const selectedAddressValidationResultIndex = useAtomValue(selectedAddressValidationResultIndexAtom)
  const authContext = useAuthenticationContext()
  const userCanDeleteAddressScheduled = authContext.has(DELETE_LOCATION_CHANGE_SCOPE)

  const locationChange = useAtomValue(currentLocationChangeAtom)
  const currentLocationChange = toLocationChangeWithAddress(locationChange)

  const {
    control,
    handleSubmit,
    formState: { errors, isValid }
  } = useForm<FormInputs>({
    values: {
      scheduledDate: currentLocationChange?.effectiveAt
        ? DateTime.fromISO(currentLocationChange.effectiveAt, { zone: currentLocationChange.address?.timeZone })
        : null
    }
  })

  const isPending = currentLocationChange?.state === 'pending'

  const dates = useMemo(() => {
    const now = DateTime.now().startOf('day')
    const maxDate = now.plus({ years: 1 })
    return { now, maxDate }
  }, [])

  const { now, maxDate } = dates

  const overviewPagePath = locationChangeId !== undefined ? `../../overview` : '../overview'

  const validateDate = (date: DateTime | null) => {
    if (!date) return true
    return date >= now && date <= maxDate
  }

  const handleCloseDialog = () => {
    setIsDeleteDialogOpen(false)
  }

  const handleConfirmDeletion = async () => {
    try {
      setIsDeleting(true)

      const timezone = currentLocationChange.address?.timeZone
      const scheduledDate = DateTime.now().setZone(timezone, { keepLocalTime: true }).set({ hour: 9, minute: 0, second: 0, millisecond: 0 })
      const effectiveAt = scheduledDate?.toISO() || ''
      const locationChange: UpdateLocationChange = {
        address: currentLocationChange.address,
        effectiveAt: effectiveAt,
        state: LocationChangeState.cancelled
      }

      await updateLocationChangeMutation.mutateAsync({
        locationId: location ? location.id : '',
        locationChangeId: currentLocationChange.id || '',
        locationChange: locationChange
      })

      // Send Delete Metric
      await sendDeleteLocationChangeMetric(location, user)

      navigate(overviewPagePath)
      toast('Address change successfully cancelled.', { variant: 'success' })
    } catch (error) {
      logger.error('Error cancelling address change', error as Error)
    } finally {
      setIsDeleting(false)
    }
  }

  const handleDeleteScheduled = async () => {
    setIsDeleteDialogOpen(true)
  }

  const onSubmit = async (data: FormInputs) => {
    const timezone = currentLocationChange!.address!.timeZone
    const scheduledDate = data.scheduledDate?.setZone(timezone, { keepLocalTime: true }).set({ hour: 9, minute: 0, second: 0, millisecond: 0 })
    const effectiveAt = scheduledDate?.toISO() || ''

    if (currentLocationChange!.id) {
      const locationChange: UpdateLocationChange = {
        address: currentLocationChange.address as MonitoringLocationChangeAddress,
        effectiveAt: effectiveAt,
        state: LocationChangeState.pending
      }
      try {
        await updateLocationChangeMutation.mutateAsync({
          locationId: location ? location.id : '',
          locationChangeId: currentLocationChange!.id,
          locationChange: locationChange
        })

        // Send Update Metric
        await sendUpdateLocationChangeMetric(location, user, effectiveAt)

        toast('Address change successfully updated.', { variant: 'success' })
      } catch (error) {
        logger.error('Error updating address change', error as Error)
      }
    } else {
      const locationChange: CreateLocationChange = {
        address: currentLocationChange.address,
        effectiveAt
      }
      try {
        await createLocationChangeMutation.mutateAsync({
          locationId: location ? location.id : '',
          locationChange: locationChange
        })

        // Send Create Metric
        await sendCreateLocationChangeMetric(location, user, effectiveAt)

        toast('Address change successfully created.', { variant: 'success' })
      } catch (error) {
        logger.error('Error creating new address change', error as Error)
      }
    }
    navigate(overviewPagePath)
  }

  return (
    <Page title={'Address Change Scheduler'}>
      <Stack flexGrow={1} useFlexGap sx={{ bgcolor: 'var(--bg-surface)', boxShadow: 1, borderRadius: 'var(--border-radius-s)' }}>
        <Stack>
          <Typography variant="h2" fontWeight="700" color="text.primary" sx={{ padding: 'var(--spacing-s)' }}>
            Scheduled Address Changes
          </Typography>
        </Stack>
        <Stack
          flexDirection={'row'}
          alignItems="center"
          justifyContent="flex-start"
          sx={{ padding: 'var(--spacing-m) var(--spacing-l)', gap: 'var(--spacing-m)' }}
        >
          <Box>
            <Typography fontWeight="500" color="text.primary" sx={{ padding: 'var(--spacing-s)', paddingBottom: 'var(--spacing-none)' }}>
              {currentLocationChange.address.street1}
            </Typography>
            {currentLocationChange.address.street2 !== '' && (
              <Typography fontWeight="500" color="text.primary" sx={{ paddingLeft: 'var(--spacing-s)', paddingBottom: 'var(--spacing-none)' }}>
                {currentLocationChange.address.street2}
              </Typography>
            )}
            <Typography fontWeight="500" color="text.primary" sx={{ padding: 'var(--spacing-s)', paddingTop: 'var(--spacing-none)' }}>
              {currentLocationChange.address.city}, {currentLocationChange.address.state} {currentLocationChange.address.postalCode}
            </Typography>
          </Box>
          <IconButton
            component={ReactRouterLink}
            to={`../${addressValidationPaths.addressForm}`}
            aria-label="Edit address change"
            data-testid="edit-change-address-button"
            sx={{ marginRight: 'var(--spacing-s)', marginTop: 'var(--spacing-none)' }}
          >
            <EditIcon color="primary" />
          </IconButton>
          <ValidationStatusChip
            addressValidationResult={addressValidationResults}
            selectedAddressValidationResultIndex={selectedAddressValidationResultIndex}
          />
        </Stack>
        <Divider />
        <Box component="form" onSubmit={handleSubmit(onSubmit)}>
          <Stack sx={{ padding: 'var(--spacing-s)', flexDirection: 'column', alignItems: 'flex-start' }}>
            <Typography
              fontSize="var(--size-l)"
              fontWeight="500"
              color="text.primary"
              sx={{ padding: 'var(--spacing-2xs)', paddingBottom: 'var(--spacing-m)' }}
            >
              Pick A Date For The Change
            </Typography>
            <Controller
              name="scheduledDate"
              control={control}
              rules={{
                required: 'Please select a date',
                validate: {
                  dateRange: value => validateDate(value) || 'Date must be within the next year'
                }
              }}
              render={({ field }) => (
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
                  <DatePicker
                    {...field}
                    label="Select Date"
                    minDate={now}
                    maxDate={maxDate}
                    disablePast
                    slotProps={{
                      textField: {
                        inputProps: {
                          'data-testid': 'date-picker-input'
                        },
                        error: !!errors.scheduledDate,
                        helperText: errors.scheduledDate ? errors.scheduledDate.message : undefined
                      }
                    }}
                    onError={error => {
                      if (error === 'maxDate' || error === 'minDate') {
                        field.onChange(null)
                      }
                    }}
                  />
                  <Typography
                    component="span"
                    variant="body2"
                    color="text.secondary"
                    sx={{ mt: 1, width: '100%', whiteSpace: 'normal', overflowWrap: 'break-word' }}
                  >
                    Changes will take place between 9 and 10 am on the scheduled day in the local time of the new address
                  </Typography>
                </Box>
              )}
            />
          </Stack>

          <Divider />
          <Stack data-testid="date-picker-actions" direction="row" justifyContent="flex-end" spacing={2} sx={{ padding: 'var(--spacing-m)' }}>
            {isPending && (
              <Button
                data-testid="delete-scheduled-change"
                disabled={!userCanDeleteAddressScheduled}
                variant="text"
                startIcon={<DeleteIcon />}
                onClick={handleDeleteScheduled}
              >
                Delete Scheduled Change
              </Button>
            )}
            <Button component={ReactRouterLink} to={overviewPagePath} type="button" variant="outlined" data-testid="date-picker-cancel">
              Cancel
            </Button>
            <LoadingButton
              type="submit"
              data-testid="date-picker-submit"
              variant="contained"
              loading={createLocationChangeMutation.isPending || updateLocationChangeMutation.isPending}
              disabled={!isValid}
            >
              Submit
            </LoadingButton>
          </Stack>
        </Box>
      </Stack>
      <DeleteSchedulerDialog isOpen={isDeleteDialogOpen} isDeleting={isDeleting} onClose={handleCloseDialog} onConfirm={handleConfirmDeletion} />
    </Page>
  )
}

export { DatePickerPage }
